import { type ReactNode } from 'react'

import { Icon } from './Icon'
import { cn } from '../lib/cn'

/**
 * Count chip — ported from `FgCountChip` in `designs/system.jsx` (~526),
 * derived from node `156:2274`.
 *
 * ┌─────────────────────────────────────────────────────────────────────┐
 * │ THIS IS NOT THE FILTER CHIP. ADR-0036 retired the filter chip        │
 * │ *control*; the visual *vocabulary* survives as a read-only display   │
 * │ element — an active-filter readout, a result count, a category tally.│
 * │ Nothing in M17 wires this to a filter action, and nothing should.    │
 * │ If you are reaching for this to build a filter row, stop and read    │
 * │ ADR-0036 first — the control was removed on purpose.                 │
 * └─────────────────────────────────────────────────────────────────────┘
 *
 * PORTED: `green-900` fill with `green-50` ink when selected, `--bg-app` with
 * a `--line-1` border when not, `--r-4` (16px) radius, 4/8 padding.
 *
 * `selected` is expressed with `aria-pressed`, not just paint, so the state is
 * available to a screen reader. It also carries a check glyph — see below.
 */
export interface CountChipProps {
  label: string
  /** Optional tally rendered after the label, tabular so rows align. */
  count?: number
  selected?: boolean
  disabled?: boolean
  onClick?: () => void
  /**
   * Renders a `<span>` instead of a `<button>` — the pure display role.
   *
   * M17-T08 needed this for the results page's active-filter readout. ADR-0036
   * retired the filter *control*, so a readout rendered as a button would be a
   * dead control sitting in the tab order, announced as pressable, doing
   * nothing — exactly the thing the ADR removed. This is the honest shape for
   * "here is what you are looking at" as opposed to "press to change it".
   */
  readOnly?: boolean
  className?: string
}

export function CountChip({
  label,
  count,
  selected,
  disabled,
  onClick,
  readOnly,
  className,
}: CountChipProps): ReactNode {
  const paint = cn(
    'inline-flex items-center gap-1 rounded-r3 border px-2 py-1 text-[14px] leading-5',
    selected
      ? 'border-accent-ink bg-accent-ink text-accent-bg'
      : 'border-line-1 bg-bg-app text-ink-1',
    className,
  )

  const body = (
    <>
      {/* M17-T06 measured the palette's greyscale collapse: desaturated, the
          accent ramp separates almost nothing. Selected therefore carries a
          check glyph as well as a fill — shape, not just colour. The fill's
          lightness (accent-ink on white) does survive desaturation, but this
          costs 12px and removes the question. */}
      {selected ? <Icon name="check" size={12} aria-hidden="true" /> : null}
      <span className="px-1">{label}</span>
      {count !== undefined ? (
        <span className="tabular-nums">{count}</span>
      ) : null}
    </>
  )

  if (readOnly) return <span className={paint}>{body}</span>

  return (
    <button
      type="button"
      aria-pressed={!!selected}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        paint,
        // DERIVED — Figma draws no disabled state (G31). Reduced opacity plus
        // the native `disabled` attribute, which is what actually keeps it out
        // of the tab order and announces it.
        disabled
          ? 'cursor-not-allowed opacity-45'
          : 'cursor-pointer hover:border-line-2',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
      )}
    >
      {body}
    </button>
  )
}
